import type { AxiosRequestConfig } from 'axios';
import { monitoringChannelOptionMatches } from './monitoringTemporalContract';
import { validateCollectionOverview, validateCollectionContents, type CollectionContentFilter } from './collectionOverview';
import { storeAuthoringMatches, validStoreSelection, validateStoreCandidates, type AmazonStoreSelection, type AmazonStoreCandidatePage } from './amazonStore';
import { HISTORY_CONFIRMATION_ACTION, historyConfirmationWrite, validateHistoryConfirmationPreview,
    validateHistoryConfirmationReceipt, type HistoryConfirmationIntent } from './historyConfirmation';
import { collectionHistoryPreviewMatches, collectionHistoryScopesMatch, validCollectionHistories, validCollectionHistoryPlans,
    type CollectionHistoryPolicy, type MetaAdvertiserHistoryPolicy,
    type AnyCollectionHistoryPlan as CollectionHistoryPlan } from './collectionHistory';
import { commentHistoryPreviewMatches, commentHistoryScopesMatch, needsCommentHistoryPreview, validCommentHistories, validCommentHistoryPreview,
    type CommentHistoryPolicy, type CommentHistoryPreview } from './commentHistory';
import { needsNativeHistoryPreview, type NativeDateHistoryPolicy } from './nativeDateHistory';
import { discoveryDatePreviewMatches, discoveryDateScopesMatch, requestsDiscoveryDates, validDiscoveryDateScopes, type DiscoveryDates } from './discoveryDates';
import { validConsumptionPreview, type MarketConsumptionPreview } from './consumptionPreview';
import { analysisConfigMatches, validAnalysisConfig, type MarketAnalysisConfig } from './analysisConfig';
import { reportConfigMatches, validReportConfig, type MarketReportConfig } from './reportConfig';
import { notificationConfigMatches, validNotificationConfig, type MarketNotificationConfig } from './notificationConfig';
import { validateReportNotifications } from './reportNotifications';
export type { MarketReportNotification, MarketReportNotifications } from './reportNotifications';
import { mediaConfigMatches, validMediaConfig, type MarketMediaConfig } from './mediaConfig';
import { validateMediaResults } from './mediaResults';
import { creativeConfigMatches, validCreativeConfig, type MarketCreativeConfig } from './creativeConfig';
import { validateCreativeResults } from './creativeResults';
import { validLandingConfig, landingConfigMatches, type MarketLandingConfig } from './landingConfig';
import { validateLandingResults } from './landingResults';
import { validateMonitorUsage } from './monitorUsage';
export type { MarketMonitorUsage } from './monitorUsage';
export type { MarketLandingConfig } from './landingConfig';
export type { MarketLandingResults } from './landingResults';
import { mediaInspectionConfigMatches, validMediaInspectionConfig, type MarketMediaInspectionConfig } from './mediaInspectionConfig';
export type { MarketMediaInspectionConfig } from './mediaInspectionConfig';
import { validateMediaInspectionResults } from './mediaInspectionResults';
export type { MarketMediaInspectionResults } from './mediaInspectionResults';
export type { MarketCreativeConfig } from './creativeConfig';
export type { MarketCreativeResults } from './creativeResults';
export type { MarketMediaConfig } from './mediaConfig';
export type { MarketMediaResults } from './mediaResults';
export type { MarketAnalysisConfig } from './analysisConfig';
export type { MarketConsumptionPreview } from './consumptionPreview';
import { validateInsights, validateInsightEvidence } from './insights';
import { validateProductEvidence } from './products';
import { amazonProductScopeEchoesRequest } from './amazonProductScope';
import { youtubeTargetScopeEchoesRequest } from './youtubeTargetScope';
import { isYoutubeCapability } from './youtubeTarget';
import { normalizeAccountPostFilters } from './accountPostFilters';
import { normalizeYoutubeResultFilters, type MarketYoutubeResultFilters } from './youtubeResultFilters';
import { amazonFilterScopesMatch, isAmazonCapability, normalizeAmazonResultFilters, type MarketAmazonResultFilters } from './amazonResultFilters';
import { AMAZON_REVIEWS_CAPABILITY, amazonReviewScopesMatch, validAmazonReviewScopes, type MarketAmazonReviewFilters } from './amazonReviews';
import { isMetaCapability, normalizeMetaResultFilters, type MarketMetaResultFilters } from './metaResultFilters';
import { businessSubjectDeclarationsMatch, validBusinessSubject, type MarketBusinessSubject } from './businessSubject';
export type { MarketBusinessSubject } from './businessSubject';
export type { MarketProductEvidencePage } from './products';
export type { MarketInsights, MarketInsightEvidencePage } from './insights';

import config from '../../config';
import { Api, type ApiConfig } from '../api';
import {
    BUSTLY_CORE_PRODUCT_CODE,
    PRODUCT_CONTEXT_HEADER,
} from '../productContext';

export type MarketCoverageMode = 'estimated_open_world' | 'enumerated';
export interface MarketRunBudget {
    contract_version: 'market-run-budget/v1';
    unit: 'unique_canonical_records';
    max_records: number;
}
export interface MarketDeliveryState {
    contract_version: 'market-delivery/v1';
    lifecycle: 'running' | 'settled';
    delivery: 'pending' | 'ready' | 'partial' | 'unavailable';
    coverage: 'budget_limited' | 'archive_incomplete' | 'open_world_unknown';
    recovery_action: 'wait' | 'retry_archive' | 'attention' | 'none';
    records_accepted: number | null;
    records_rejected: number | null;
    records_budget_excluded: number | null;
    records_duplicate: number | null;
    unique_records_admitted: number | null;
    evidence_published?: boolean;
    publication_current?: boolean;
    run_budget: MarketRunBudget | null;
}
export type MarketCadence = 'manual' | 'daily' | 'twice_daily' | 'weekly';
export type MarketMediaType = 'all' | 'image' | 'video';
const MARKET_SOURCE_PLATFORMS = [
    'meta', 'tiktok', 'reddit', 'x', 'youtube', 'facebook', 'instagram',
    'google_search', 'google_trends', 'amazon',
] as const;
export type MarketSourcePlatform = (typeof MARKET_SOURCE_PLATFORMS)[number];
export type MarketQuerySliceSource = MarketSourcePlatform | 'reddit_comments';
export type MarketQueryStatus =
    | 'accepted'
    | 'running'
    | 'ready'
    | 'partial'
    | 'failed';
export type MarketExecutionMode = 'fake' | 'provider';
export type MarketRuntimeExecutionMode = 'disabled' | MarketExecutionMode;
export type MarketRuntimeState = 'ready' | 'blocked' | 'unavailable';

export interface MarketRuntimeStatus {
    status: MarketRuntimeState;
    execution_mode: MarketRuntimeExecutionMode | null;
    can_run: boolean;
    reason_code: string;
}
export type MarketCapabilityAvailability = 'enabled' | 'disabled' | 'degraded';
export interface MarketCapabilityScope {
    mode: string;
    values: string[];
}
export interface MarketCapability {
    run_budget_versions?: string[];
    capability: string;
    platform: MarketSourcePlatform;
    source_system: string;
    availability: MarketCapabilityAvailability;
    execution_mode: MarketRuntimeExecutionMode | null;
    reason_code: string;
    rollout_stage: string;
    region_scope: MarketCapabilityScope;
    language_scope: MarketCapabilityScope;
}
export type MarketWatchTargetType = 'brand' | 'product' | 'topic' | 'ad_creative';
export type MarketHeatStage =
    | 'baseline_building'
    | 'insufficient_data'
    | 'stable'
    | 'emerging'
    | 'rising'
    | 'peaking'
    | 'cooling';
export type MarketHeatWindow = '24h' | '7d' | '30d';

export interface MarketQuerySliceWrite {
    slice_id: string;
    source: MarketQuerySliceSource;
    query: string;
    active_status: 'active';
    media_type: MarketMediaType;
    stratum: string;
    days_back?: number;
}

export type MarketMonitorSubjectType =
    | 'brand'
    | 'product'
    | 'topic'
    | 'ad_creative';
export type MarketMonitorTermRole = 'primary' | 'alias' | 'exclude';
export interface MarketMonitorTermWrite {
    match_mode?: 'all_terms';
    parser_version?: 'keyword-tokens/v1';
    parsed_terms?: string[];
    value: string;
    role: MarketMonitorTermRole;
    language?: string;
}
export interface MarketMonitorIdentifier {
    business_subject?: MarketBusinessSubject | null;
    kind:
        | 'domain'
        | 'social_account'
        | 'source_url'
        | 'asin'
        | 'source_entity_id'
        | 'source_ad_id'
        | 'creative_fingerprint'
        | 'landing_url';
    value: string;
    source_system?: string;
}
export interface MarketMonitorScope {
    kind: 'unfiltered' | 'country' | 'marketplace';
    value?: string;
}
export interface MarketMonitorSourceScope extends DiscoveryDates {
    google_options?: import('./google').GoogleOptions;
    capability: string;
    source_system: string;
    scope: MarketMonitorScope;
    max_records_per_slice?: number;
    review_variant_scope?: "all" | "current" | null;
    media_type?: 'all' | 'image' | 'video';
    active_status?: 'active' | 'all' | 'inactive';
    include_public_details?: boolean;
    post_window?: 'past_hour' | 'past_day' | 'past_week' | 'past_month' | 'past_year' | 'all_time';
    collection_history?: CollectionHistoryPolicy | MetaAdvertiserHistoryPolicy | NativeDateHistoryPolicy | null;
    result_filters?: import('./accountPostFilters').MarketAccountPostFilters & MarketMetaResultFilters & MarketYoutubeResultFilters & MarketAmazonResultFilters & MarketAmazonReviewFilters & { subreddits?: string[]; min_score?: number | null; published_from?: string; published_to?: string };
    result_sort?: 'collection_order' | 'published_desc' | 'score_desc' | 'comments_desc' | import('./metaPresentation').MetaResultSort;
    result_grouping?: import('./metaPresentation').MetaResultGrouping;
    advertiser_period?: 'all' | 'last24h' | 'last7d' | 'last14d' | 'last30d';
    advertiser_sort?: 'most_recent' | 'impressions_desc';
    comments?: { max_threads: number; max_comments_per_thread: number; days_back: number; collection_history?: CommentHistoryPolicy | null };
}
export interface MarketMonitorSourceAvailability {
    capability: string;
    source_system: string;
    scope: MarketMonitorScope;
    status: 'ready' | 'unavailable';
    reason_code: string | null;
}

export interface MarketMonitoringPeriod {
    start_date: string;
    end_date: string;
    time_zone: string;
    /** Stable wall-clock anchor used to keep recurring runs on the selected creation-time slot. */
    anchor_at?: string;
}

export interface MarketMonitorCollectionPolicy {
    schema_version: 'market-monitor-collection-policy/v1';
    initial_mode: 'from_period_start';
    incremental_overlap_hours: number;
    recovery_horizon_days: number;
}

export interface MarketMonitorWrite {
    amazon_store_selection?: AmazonStoreSelection | null;
    report_config?: MarketReportConfig;
    notification_config?: MarketNotificationConfig;
    landing_config?: MarketLandingConfig;
    media_inspection_config?: MarketMediaInspectionConfig;
    creative_config?: MarketCreativeConfig;
    media_config?: MarketMediaConfig;
    analysis_config?: MarketAnalysisConfig;
    business_subject?: MarketBusinessSubject | null;
    run_budget?: MarketRunBudget;
    monitoring_period?: MarketMonitoringPeriod | null;
    run_immediately?: boolean;
    contract_version: 'market-monitor/v1';
    name: string;
    subject_type: MarketMonitorSubjectType;
    enabled: boolean;
    terms: MarketMonitorTermWrite[];
    identifiers: MarketMonitorIdentifier[];
    source_scopes: MarketMonitorSourceScope[];
    cadence: MarketCadence;
}

export interface MarketObservationPlan {
    plan_id: string;
    version: number;
    coverage_mode: MarketCoverageMode;
    planned_slices: number;
    expected_targets: number | null;
    effective_from: string;
}

export interface MarketMonitor {
    created_by?: string | null;
    creator_name?: string | null;
    store_discovery?: 'amazon-store-discovery/v1';
    amazon_store_selection?: AmazonStoreSelection | null;
    report_config?: MarketReportConfig;
    notification_config?: MarketNotificationConfig;
    landing_config?: MarketLandingConfig;
    media_inspection_config?: MarketMediaInspectionConfig;
    creative_config?: MarketCreativeConfig;
    media_config?: MarketMediaConfig;
    analysis_config?: MarketAnalysisConfig;
    business_subject?: MarketBusinessSubject | null;
    run_budget?: MarketRunBudget | null;
    monitoring_period?: MarketMonitoringPeriod | null;
    collection_policy?: MarketMonitorCollectionPolicy | null;
    contract_version: '1.0.0' | 'market-monitor/v1';
    monitor_id: string;
    name: string;
    subject_type?: MarketMonitorSubjectType;
    enabled: boolean;
    watch_target_id: string | null;
    coverage_mode: MarketCoverageMode;
    region: string;
    category: string;
    cadence: MarketCadence;
    advertiser_page_urls: string[];
    query_slices: MarketQuerySliceWrite[];
    max_provider_records_per_run: number;
    terms?: MarketMonitorTermWrite[];
    identifiers?: MarketMonitorIdentifier[];
    source_scopes?: MarketMonitorSourceScope[];
    source_availability?: MarketMonitorSourceAvailability[];
    version: number;
    current_plan: MarketObservationPlan;
    last_success_at: string | null;
    next_run_at: string | null;
    initial_query?: MarketQuery | null;
    created_at: string;
    updated_at: string;
}

export interface MarketMonitorPreview {
    discovery_date_range_support?: 'instagram-discovery-dates/v1';
    youtube_discovery_date_range_support?: 'youtube-discovery-dates/v1';
    collection_history_support?: 'reddit-history/v1';
    comment_history_support?: 'reddit-comment-history/v1';
    comment_history_preview?: CommentHistoryPreview[];
    native_date_history_support?: 'native-date-history/v1';
    collection_history_preview?: CollectionHistoryPlan[];
    normalized_monitor: Record<string, unknown>;
    logical_slices: number;
    physical_requests: number | null;
    max_records: number;
    budget_allocations?: Array<{ source_system: string; capability: string; scope: MarketMonitorScope; logical_slices: number; physical_requests: number; max_records: number; comment_records: number }>;
    consumption_preview?: MarketConsumptionPreview;
    /** v1 has no verified supplier quote producer; planning allowances are not prices. */
    estimated_cost_micros: null;
    estimated_recurring_cost_micros: null;
    cost_currency: null;
    source_availability: MarketMonitorSourceAvailability[];
    existing_monitor_id: string | null;
    related_monitor_ids?: string[];
    warnings: string[];
}

export interface MarketTargetAccounting {
    planned_targets?: number | null;
    attempted_targets?: number | null;
    completed_targets?: number | null;
    failed_targets?: number | null;
    skipped_targets?: number | null;
    [key: string]: unknown;
}

export interface MarketQuery {
    delivery_state?: MarketDeliveryState | null;
    query_id: string;
    job_status: MarketQueryStatus;
    execution_mode: MarketExecutionMode | null;
    observation_plan: MarketObservationPlan;
    monitor_version?: number | null;
    superseded?: boolean | null;
    coverage_mode: MarketCoverageMode;
    target_accounting: MarketTargetAccounting | null;
    coverage_revision: number | null;
    freshness_at: string | null;
    budget_limited: boolean;
    unknowns: string[];
    source_results: MarketSourceResult[];
    evidence_count: number | null;
    records_observed: number | null;
    records_archived: number | null;
    provider_declared_total: number | null;
    pagination_complete: boolean | null;
    archive_complete: boolean | null;
    compliance_fresh: boolean | null;
    poll_after_seconds: number | null;
    submitted_at: string;
    completed_at: string | null;
    cost_micros: number | null;
    error: string | null;
}

export interface MarketSourceResult {
    collection_history_plans?: CollectionHistoryPlan[];
    delivery_state?: MarketDeliveryState | null;
    capability: string;
    platform: MarketSourcePlatform;
    source_system: string;
    job_status: 'running' | 'ready' | 'partial' | 'failed';
    execution_mode: MarketExecutionMode | null;
    planned_slices: number;
    completed_slices: number;
    failed_slices: number;
    pending_slices: number;
    records_observed: number | null;
    records_archived: number | null;
    provider_declared_total: number | null;
    pagination_complete: boolean | null;
    freshness_at: string | null;
    archive_complete: boolean | null;
    compliance_fresh: boolean | null;
    repair_state: 'pending' | 'required' | 'not_required' | 'unavailable';
    unknowns: string[];
}

export interface MarketTargetIdentifierWrite {
    identifier_type: string;
    source_system: string;
    value: string;
    confidence: number;
}

export interface MarketWatchTargetWrite {
    target_type: MarketWatchTargetType;
    display_name: string;
    category: string;
    regions: string[];
    languages: string[];
    status: 'active' | 'paused';
    identifiers: MarketTargetIdentifierWrite[];
}

export interface MarketWatchTarget {
    id: string;
    target_type: MarketWatchTargetType;
    display_name: string;
    category: string | null;
    regions: string[];
    languages: string[];
    status: 'active' | 'paused' | 'retired';
    version: number;
    updated_at: string;
}

export interface MarketHeat {
    target_id: string;
    window: string;
    as_of: string | null;
    stage: MarketHeatStage;
    score: number | null;
    confidence: number | null;
    paid_signal_components: Record<string, unknown> | null;
    organic_signal_components: Record<string, unknown> | null;
    cross_source_components: Record<string, unknown> | null;
    coverage: Record<string, unknown> | null;
    baseline_version: string | null;
    unknowns: string[];
}

export interface MarketMonitorSummary {
    /** Deduplicated readable content, not the latest collection batch. */
    collected_content_count?: number | null;
    monitor: MarketMonitor;
    latest_run: MarketQuery | null;
    watch_target: MarketWatchTarget | null;
    heat: MarketHeat | null;
    heat_window: string;
    /** Client-side read diagnostics; never infer collection quality from these. */
    display_issues?: Array<'watch_target' | 'heat'>;
    capabilities: MarketCapability[];
    data_quality: {
        status:
            | 'unavailable'
            | 'running'
            | 'partial'
            | 'baseline_building'
            | 'ready';
        unknowns: string[];
    };
}

type IdFactory = () => string;

export class MarketIntelligenceResponseError extends Error {
    readonly code = 'MARKET_INTELLIGENCE_INVALID_RESPONSE';

    constructor(resource: string) {
        super(`Market Intelligence returned an invalid ${resource} response.`);
        this.name = 'MarketIntelligenceResponseError';
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value && typeof value === 'object' && !Array.isArray(value));

const validMonitoringPeriod = (value: unknown): value is MarketMonitoringPeriod | null | undefined => {
    if (value == null) return true;
    if (!isRecord(value) || Object.keys(value).some(key => !['start_date', 'end_date', 'time_zone', 'anchor_at'].includes(key))
        || typeof value.start_date !== 'string' || typeof value.end_date !== 'string'
        || typeof value.time_zone !== 'string' || value.start_date > value.end_date
        || value.anchor_at !== undefined && (typeof value.anchor_at !== 'string'
            || !/(?:Z|[+-]\d{2}:\d{2})$/.test(value.anchor_at) || !Number.isFinite(Date.parse(value.anchor_at)))) return false;
    const date = (text: string) => /^\d{4}-\d{2}-\d{2}$/.test(text)
        && new Date(text + 'T00:00:00Z').toISOString().slice(0, 10) === text;
    try { return date(value.start_date) && date(value.end_date)
        && Boolean(new Intl.DateTimeFormat('en', { timeZone: value.time_zone }).format()); }
    catch { return false; }
};

const validCollectionPolicy = (value: unknown): value is MarketMonitorCollectionPolicy | null | undefined =>
    value == null || isRecord(value)
    && value.schema_version === 'market-monitor-collection-policy/v1'
    && value.initial_mode === 'from_period_start'
    && value.incremental_overlap_hours === 168
    && value.recovery_horizon_days === 7;

// OffsetDateTime normalizes offsets and omits zero seconds/fractions. Compare
// the instant while preserving sub-millisecond precision in saved anchors.
const monitoringAnchorMatches = (requested: string | undefined, echoed: string | undefined): boolean => {
    if (requested === undefined || echoed === undefined) return requested === echoed;
    const remainder = (value: string) => (value.match(/\.(\d+)(?:Z|[+-]\d{2}:\d{2})$/)?.[1] ?? '')
        .slice(3).replace(/0+$/, '');
    return Date.parse(requested) === Date.parse(echoed) && remainder(requested) === remainder(echoed);
};

const monitoringPeriodMatches = (requested: MarketMonitoringPeriod | null | undefined, echoed: unknown): boolean => {
    if (requested === undefined) return true;
    if (requested === null) return echoed === null;
    return validMonitoringPeriod(echoed) && echoed != null
        && requested.start_date === echoed.start_date
        && requested.end_date === echoed.end_date
        && requested.time_zone === echoed.time_zone
        && monitoringAnchorMatches(requested.anchor_at, echoed.anchor_at);
};

const validateCapability = (value: unknown): MarketCapability => {
    if (
        !isRecord(value) ||
        typeof value.capability !== 'string' ||
        !MARKET_SOURCE_PLATFORMS.some(platform => platform === value.platform) ||
        typeof value.source_system !== 'string' ||
        !['enabled', 'disabled', 'degraded'].includes(
            String(value.availability),
        ) ||
        (value.execution_mode !== null &&
            !['disabled', 'fake', 'provider'].includes(
                String(value.execution_mode),
            )) ||
        typeof value.reason_code !== 'string' ||
        typeof value.rollout_stage !== 'string' ||
        !isRecord(value.region_scope) ||
        typeof value.region_scope.mode !== 'string' ||
        !Array.isArray(value.region_scope.values) ||
        !isRecord(value.language_scope) ||
        typeof value.language_scope.mode !== 'string' ||
        !Array.isArray(value.language_scope.values)
    ) {
        throw new MarketIntelligenceResponseError('capability');
    }
    return value as unknown as MarketCapability;
};

const validateMonitor = (value: unknown): MarketMonitor => {
    const targetContract =
        isRecord(value) && value.contract_version === 'market-monitor/v1';
    if (
        !isRecord(value) ||
        (value.contract_version !== '1.0.0' && !targetContract) ||
        typeof value.monitor_id !== 'string' ||
        typeof value.name !== 'string' ||
        !validStoreSelection(value.amazon_store_selection) ||
        !validMonitoringPeriod(value.monitoring_period) ||
        !validCollectionPolicy(value.collection_policy) ||
        !validAnalysisConfig(value.analysis_config) ||
        !validCommentHistories(value.source_scopes) || !validCollectionHistories(value.source_scopes) || !validDiscoveryDateScopes(value.source_scopes) || !validAmazonReviewScopes(value.source_scopes) ||
        !validReportConfig(value.report_config) ||
        !validNotificationConfig(value.notification_config) ||
        !validLandingConfig(value.landing_config) || !validMediaConfig(value.media_config) || !validCreativeConfig(value.creative_config) || !validMediaInspectionConfig(value.media_inspection_config) ||
        !validBusinessSubject(value.business_subject) ||
        (Array.isArray(value.identifiers) && value.identifiers.some(item => !isRecord(item) || !validBusinessSubject(item.business_subject))) ||
        (value.watch_target_id !== null &&
            typeof value.watch_target_id !== 'string') ||
        typeof value.version !== 'number' ||
        (targetContract &&
            (typeof value.subject_type !== 'string' ||
                !Array.isArray(value.terms) ||
                !Array.isArray(value.source_scopes))) ||
        (!targetContract &&
            (!Array.isArray(value.advertiser_page_urls) ||
                !Array.isArray(value.query_slices) ||
                value.query_slices.some(
                    (slice) =>
                        !isRecord(slice) || typeof slice.query !== 'string',
                ) ||
                typeof value.max_provider_records_per_run !== 'number')) ||
        !isRecord(value.current_plan) ||
        typeof value.current_plan.planned_slices !== 'number'
    ) {
        throw new MarketIntelligenceResponseError('monitor');
    }
    const initialQuery = value.initial_query == null
        ? null
        : validateQuery(value.initial_query);
    return {
        ...(value as unknown as MarketMonitor),
        initial_query: initialQuery,
        watch_target_id:
            typeof value.watch_target_id === 'string'
                ? value.watch_target_id
                : null,
        coverage_mode:
            value.coverage_mode === 'enumerated'
                ? 'enumerated'
                : 'estimated_open_world',
        region: typeof value.region === 'string' ? value.region : 'ALL',
        category: typeof value.category === 'string' ? value.category : '',
        advertiser_page_urls: Array.isArray(value.advertiser_page_urls)
            ? (value.advertiser_page_urls as string[])
            : [],
        query_slices: Array.isArray(value.query_slices)
            ? (value.query_slices as MarketQuerySliceWrite[])
            : [],
        max_provider_records_per_run:
            typeof value.max_provider_records_per_run === 'number'
                ? value.max_provider_records_per_run
                : Math.max(
                      0,
                      ...(
                          (value.source_scopes as
                              | MarketMonitorSourceScope[]
                              | undefined) || []
                      ).map((scope) => scope.max_records_per_slice ?? 0),
                  ),
    };
};

const validateMonitorPreview = (value: unknown): MarketMonitorPreview => {
    if (
        !isRecord(value) ||
        !isRecord(value.normalized_monitor) ||
        !validMonitoringPeriod(value.normalized_monitor.monitoring_period) ||
        !validCollectionPolicy(value.normalized_monitor.collection_policy) ||
        !validAnalysisConfig(value.normalized_monitor.analysis_config) ||
        !validCommentHistories(value.normalized_monitor.source_scopes) || !validCommentHistoryPreview(value.comment_history_preview) ||
        !validCollectionHistories(value.normalized_monitor.source_scopes) || !validDiscoveryDateScopes(value.normalized_monitor.source_scopes) || !validAmazonReviewScopes(value.normalized_monitor.source_scopes) ||
        !validCollectionHistoryPlans(value.collection_history_preview) ||
        !validReportConfig(value.normalized_monitor.report_config) ||
        !validNotificationConfig(value.normalized_monitor.notification_config) ||
        !validLandingConfig(value.normalized_monitor.landing_config) || !validMediaConfig(value.normalized_monitor.media_config) || !validCreativeConfig(value.normalized_monitor.creative_config) || !validMediaInspectionConfig(value.normalized_monitor.media_inspection_config) ||
        !Number.isInteger(value.logical_slices) ||
        Number(value.logical_slices) < 1 ||
        (value.physical_requests !== null &&
            !Number.isInteger(value.physical_requests)) ||
        typeof value.max_records !== 'number' ||
        value.max_records < 1 ||
        // Price provenance does not appear when an optional allocation breakdown is
        // omitted. Never accept a planning number (including zero) as a v1 quote.
        value.estimated_cost_micros !== null || value.estimated_recurring_cost_micros !== null || value.cost_currency !== null ||
        (value.consumption_preview !== undefined &&
            !validConsumptionPreview(value.consumption_preview, value.max_records,
                value.physical_requests as number | null, value.budget_allocations)) ||
        !Array.isArray(value.source_availability) ||
        value.source_availability.some(
            (source) =>
                !isRecord(source) ||
                typeof source.capability !== 'string' ||
                typeof source.source_system !== 'string' ||
                !isRecord(source.scope) ||
                !['ready', 'unavailable'].includes(String(source.status)) ||
                (source.reason_code !== null &&
                    typeof source.reason_code !== 'string'),
        ) ||
        (value.existing_monitor_id !== null &&
            typeof value.existing_monitor_id !== 'string') ||
        !Array.isArray(value.warnings) ||
        value.warnings.some((warning) => typeof warning !== 'string')
    ) {
        throw new MarketIntelligenceResponseError('monitor preview');
    }
    // Older servers may still supply internal pricing. Discard before exposing preview state.
    const { provider_price_evidence: _privatePriceEvidence, ...customerPreview } = value;
    return customerPreview as unknown as MarketMonitorPreview;
};

const validateSourceResult = (value: unknown): MarketSourceResult => {
    if (
        !isRecord(value) ||
        !validCollectionHistoryPlans(value.collection_history_plans) ||
        (Array.isArray(value.collection_history_plans) && value.collection_history_plans.some(plan =>
            plan.contract_version === 'market-native-date-history-plan/v1' || plan.contract_version === 'market-comment-history-plan/v1' ? plan.capability !== value.capability
                : value.capability !== 'reddit.posts.discover_by_keyword')) ||
        typeof value.capability !== 'string' ||
        !MARKET_SOURCE_PLATFORMS.includes(value.platform as MarketSourcePlatform) ||
        typeof value.source_system !== 'string' ||
        !['running', 'ready', 'partial', 'failed'].includes(
            String(value.job_status),
        ) ||
        (value.execution_mode !== null &&
            value.execution_mode !== 'fake' &&
            value.execution_mode !== 'provider') ||
        !['pending', 'required', 'not_required', 'unavailable'].includes(
            String(value.repair_state),
        ) ||
        ![
            'planned_slices',
            'completed_slices',
            'failed_slices',
            'pending_slices',
        ].every((key) => typeof value[key] === 'number') ||
        (value.records_observed !== null &&
            typeof value.records_observed !== 'number') ||
        (value.records_archived !== null &&
            typeof value.records_archived !== 'number') ||
        (value.provider_declared_total !== undefined &&
            value.provider_declared_total !== null &&
            typeof value.provider_declared_total !== 'number') ||
        (value.pagination_complete !== undefined &&
            value.pagination_complete !== null &&
            typeof value.pagination_complete !== 'boolean') ||
        (value.freshness_at !== null &&
            typeof value.freshness_at !== 'string') ||
        (value.archive_complete !== null &&
            typeof value.archive_complete !== 'boolean') ||
        (value.compliance_fresh !== null &&
            typeof value.compliance_fresh !== 'boolean') ||
        !Array.isArray(value.unknowns)
    ) {
        throw new MarketIntelligenceResponseError('source result');
    }
    return {
        ...(value as unknown as MarketSourceResult),
        provider_declared_total:
            typeof value.provider_declared_total === 'number'
                ? value.provider_declared_total
                : null,
        pagination_complete:
            typeof value.pagination_complete === 'boolean'
                ? value.pagination_complete
                : null,
    };
};

const validateQuery = (value: unknown): MarketQuery => {
    const validStatus =
        isRecord(value) &&
        ['accepted', 'running', 'ready', 'partial', 'failed'].includes(
            String(value.job_status),
        );
    if (
        !isRecord(value) ||
        typeof value.query_id !== 'string' ||
        !validStatus ||
        (value.execution_mode !== null &&
            value.execution_mode !== 'fake' &&
            value.execution_mode !== 'provider') ||
        !isRecord(value.observation_plan) ||
        typeof value.observation_plan.planned_slices !== 'number' ||
        (value.evidence_count !== null &&
            typeof value.evidence_count !== 'number') ||
        !Array.isArray(value.unknowns) ||
        !Array.isArray(value.source_results) ||
        (value.completed_at !== null &&
            typeof value.completed_at !== 'string') ||
        (value.cost_micros !== null && typeof value.cost_micros !== 'number')
    ) {
        throw new MarketIntelligenceResponseError('query');
    }
    const sourceResults = value.source_results.map(validateSourceResult);
    return {
        ...(value as unknown as MarketQuery),
        source_results: sourceResults,
        records_observed:
            typeof value.records_observed === 'number'
                ? value.records_observed
                : null,
        records_archived:
            typeof value.records_archived === 'number'
                ? value.records_archived
                : null,
        provider_declared_total:
            typeof value.provider_declared_total === 'number'
                ? value.provider_declared_total
                : null,
        pagination_complete:
            typeof value.pagination_complete === 'boolean'
                ? value.pagination_complete
                : null,
        archive_complete:
            typeof value.archive_complete === 'boolean'
                ? value.archive_complete
                : null,
        compliance_fresh:
            typeof value.compliance_fresh === 'boolean'
                ? value.compliance_fresh
                : null,
    };
};

const validateWatchTarget = (value: unknown): MarketWatchTarget => {
    if (
        !isRecord(value) ||
        typeof value.id !== 'string' ||
        !['brand', 'product', 'topic', 'ad_creative'].includes(
            String(value.target_type),
        ) ||
        typeof value.display_name !== 'string' ||
        !Array.isArray(value.regions) ||
        !Array.isArray(value.languages) ||
        !['active', 'paused', 'retired'].includes(String(value.status)) ||
        typeof value.version !== 'number' ||
        typeof value.updated_at !== 'string'
    ) {
        throw new MarketIntelligenceResponseError('watch target');
    }
    return value as unknown as MarketWatchTarget;
};

const validateHeat = (value: unknown): MarketHeat => {
    if (
        !isRecord(value) ||
        typeof value.target_id !== 'string' ||
        typeof value.window !== 'string' ||
        ![
            'baseline_building',
            'insufficient_data',
            'stable',
            'emerging',
            'rising',
            'peaking',
            'cooling',
        ].includes(String(value.stage)) ||
        (value.score !== null && typeof value.score !== 'number') ||
        (value.confidence !== null && typeof value.confidence !== 'number') ||
        !Array.isArray(value.unknowns) ||
        (['baseline_building', 'insufficient_data'].includes(
            String(value.stage),
        ) &&
            value.score !== null)
    ) {
        throw new MarketIntelligenceResponseError('target heat');
    }
    return value as unknown as MarketHeat;
};

const validateRuntimeStatus = (value: unknown): MarketRuntimeStatus => {
    const validStatus =
        isRecord(value) &&
        ['ready', 'blocked', 'unavailable'].includes(String(value.status));
    const validMode =
        isRecord(value) &&
        (value.execution_mode === null ||
            value.execution_mode === 'disabled' ||
            value.execution_mode === 'fake' ||
            value.execution_mode === 'provider');
    if (
        !isRecord(value) ||
        !validStatus ||
        !validMode ||
        typeof value.can_run !== 'boolean' ||
        typeof value.reason_code !== 'string' ||
        value.reason_code.length === 0 ||
        value.can_run !== (value.status === 'ready') ||
        (value.can_run &&
            (value.execution_mode === null ||
                value.execution_mode === 'disabled'))
    ) {
        throw new MarketIntelligenceResponseError('runtime status');
    }
    return value as unknown as MarketRuntimeStatus;
};

const validateMonitorSummary = (value: unknown): MarketMonitorSummary => {
    if (
        !isRecord(value) ||
        typeof value.heat_window !== 'string' ||
        !Array.isArray(value.capabilities) ||
        !isRecord(value.data_quality) ||
        typeof value.data_quality.status !== 'string' ||
        !Array.isArray(value.data_quality.unknowns)
    ) {
        throw new MarketIntelligenceResponseError('monitor summary');
    }
    // Operational state stays strict. Only display metadata may degrade locally;
    // losing a run or monitor must not imply it is safe to start a paid refresh.
    const monitor = validateMonitor(value.monitor);
    const latestRun =
        value.latest_run === null ? null : validateQuery(value.latest_run);
    const capabilities = value.capabilities.map(validateCapability);
    const displayIssues: NonNullable<MarketMonitorSummary['display_issues']> =
        [];
    const readDisplay = <T>(
        field: 'watch_target' | 'heat',
        validate: (raw: unknown) => T,
    ): T | null => {
        if (value[field] === null) return null;
        try {
            return validate(value[field]);
        } catch (error) {
            if (!(error instanceof MarketIntelligenceResponseError))
                throw error;
            displayIssues.push(field);
            return null;
        }
    };
    const watchTarget = readDisplay('watch_target', (raw) => {
        const target = validateWatchTarget(raw);
        if (target.id !== monitor.watch_target_id) {
            throw new MarketIntelligenceResponseError(
                'watch target association',
            );
        }
        return target;
    });
    const heat = readDisplay('heat', (raw) => {
        const snapshot = validateHeat(raw);
        if (
            !watchTarget ||
            snapshot.target_id !== watchTarget.id ||
            snapshot.window !== value.heat_window
        ) {
            throw new MarketIntelligenceResponseError('heat association');
        }
        return snapshot;
    });
    return {
        monitor,
        latest_run: latestRun,
        collected_content_count: typeof value.collected_content_count === 'number'
            && Number.isSafeInteger(value.collected_content_count) && value.collected_content_count >= 0
            ? value.collected_content_count : null,
        watch_target: watchTarget,
        heat,
        heat_window: value.heat_window,
        display_issues: displayIssues,
        capabilities,
        data_quality:
            value.data_quality as MarketMonitorSummary['data_quality'],
    };
};

const fallbackUuid = (): string => {
    const bytes = new Uint8Array(16);
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.getRandomValues) {
        cryptoApi.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) =>
        value.toString(16).padStart(2, '0'),
    ).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createMarketIntelligenceUuid = (): string =>
    globalThis.crypto?.randomUUID?.() || fallbackUuid();

export const createMarketIntelligenceIdempotencyKey = (): string =>
    `mi-web:${createMarketIntelligenceUuid()}`;

const requestConfig = (
    workspaceId: string,
    requestId: string,
    extra?: AxiosRequestConfig,
): AxiosRequestConfig => ({
    ...(extra || {}),
    headers: {
        ...(extra?.headers || {}),
        'X-Workspace-Id': workspaceId,
        'X-Request-Id': requestId,
        [PRODUCT_CONTEXT_HEADER]: BUSTLY_CORE_PRODUCT_CODE,
    },
});

// Platform MarketRunBudget.minimumRequired reserves records for every compiled
// keyword slice, including the other sources in a monitor that contains X.
// Target-based sources have different compilation rules; retain their existing
// negotiation until their URL/identity contracts can provide an exact count.
const KEYWORD_BUDGET_CAPABILITIES = new Set([
    'x.posts.search_recent', 'reddit.posts.discover_by_keyword', 'meta.ads.discover_by_keyword',
    'youtube.videos.discover_by_keyword', 'facebook.posts.discover_by_keyword', 'instagram.posts.discover_by_keyword',
    'amazon.products.discover_by_keyword', 'google_search.results.discover_by_keyword', 'google_trends.interest.discover_by_keyword',
]);
const minimumKeywordRunRecords = (monitor: MarketMonitorWrite): number | null => {
    if (!monitor.source_scopes.every(scope => KEYWORD_BUDGET_CAPABILITIES.has(scope.capability))) return null;
    const terms = monitor.terms.filter(term => term.role !== 'exclude').length;
    return terms * monitor.source_scopes.reduce((sum, scope) => sum + Math.max(
        scope.capability === 'x.posts.search_recent' ? 50 : 1,
        1 + (scope.comments?.max_threads ?? 0) * (scope.comments?.max_comments_per_thread ?? 0),
    ), 0);
};

export class MarketIntelligenceService {
    private readonly api: Api;
    private readonly idFactory: IdFactory;

    previewHistoryConfirmation(workspaceId: string, monitorId: string, sliceId: string, signal?: AbortSignal) {
        return this.api.post<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/collection-history/preview`,
            { slice_id: sliceId, action: HISTORY_CONFIRMATION_ACTION }, requestConfig(workspaceId, this.idFactory(), { signal }))
            .then(value => validateHistoryConfirmationPreview(value, monitorId, sliceId));
    }

    confirmHistory(workspaceId: string, monitorId: string, intent: HistoryConfirmationIntent, signal?: AbortSignal) {
        if (intent.preview.monitor_id !== monitorId) throw new MarketIntelligenceResponseError('history confirmation scope');
        return this.api.put<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/collection-history/confirmations/${encodeURIComponent(intent.id)}`,
            historyConfirmationWrite(intent), requestConfig(workspaceId, this.idFactory(), { signal,
                headers: { 'If-Match': `"mi-v${intent.preview.monitor_version}"` } }))
            .then(value => validateHistoryConfirmationReceipt(value, monitorId, intent));
    }

    getHistoryConfirmation(workspaceId: string, monitorId: string, intent: HistoryConfirmationIntent, signal?: AbortSignal) {
        if (intent.preview.monitor_id !== monitorId) throw new MarketIntelligenceResponseError('history confirmation scope');
        historyConfirmationWrite(intent); // Validate identity before constructing the private read route.
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/collection-history/confirmations/${encodeURIComponent(intent.id)}`,
            requestConfig(workspaceId, this.idFactory(), { signal }))
            .then(value => validateHistoryConfirmationReceipt(value, monitorId, intent));
    }

    replayImport(workspaceId: string, queryId: string, signal?: AbortSignal): Promise<MarketQuery> {
        return this.api.post<unknown>(
            `/api/market-intelligence/queries/${encodeURIComponent(queryId)}/replay-import`, {},
            requestConfig(workspaceId, this.idFactory(), { signal }),
        ).then(validateQuery);
    }

    getProductEvidence(workspaceId: string, monitorId: string, cursor: string | null, signal?: AbortSignal, selection: 'related' | 'all' = 'related') {
        return this.api.get<unknown>('/api/market-intelligence/context/evidence',
            requestConfig(workspaceId, this.idFactory(), { signal, params: {
                monitor_id: monitorId, source_system: 'amazon', observation_domain: 'commerce',
                ...(selection === 'all' ? { relevance: selection } : {}),
                cursor: cursor ?? undefined, limit: 100,
            } })).then(value => validateProductEvidence(value, monitorId));
    }

    getReviewEvidence(workspaceId: string, monitorId: string, cursor: string | null, signal?: AbortSignal, selection: 'related' | 'all' = 'related') {
        return this.api.get<unknown>('/api/market-intelligence/context/evidence',
            requestConfig(workspaceId, this.idFactory(), { signal, params: {
                monitor_id: monitorId, source_system: 'amazon', capability: AMAZON_REVIEWS_CAPABILITY,
                observation_domain: 'commerce_review', ...(selection === 'all' ? { relevance: selection } : {}),
                cursor: cursor ?? undefined, limit: 100,
            } })).then(value => validateProductEvidence(value, monitorId, 'commerce_review'));
    }

    getInsights(workspaceId: string, monitorId: string, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights`,
            requestConfig(workspaceId, this.idFactory(), { signal })).then(validateInsights);
    }

    getReportNotifications(workspaceId: string, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>('/api/market-intelligence/report-notifications',
            requestConfig(workspaceId, this.idFactory(), { signal, params: { cursor: cursor ?? undefined } }))
            .then(value => validateReportNotifications(value, workspaceId, cursor));
    }

    getInsightReport(workspaceId: string, monitorId: string, version: string, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}`,
            requestConfig(workspaceId, this.idFactory(), { signal })).then(validateInsights).then(value => {
                if (value.monitor_id !== monitorId || value.insight?.analysis_version !== version
                    || value.update.analysis_version !== version || value.update.status !== 'ready' || value.model_enabled || value.stale)
                    throw new MarketIntelligenceResponseError('pinned report');
                return value;
            });
    }

    getMediaResults(workspaceId: string, monitorId: string, version: string, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}/media`,
            requestConfig(workspaceId, this.idFactory(), { signal, params: { after: cursor ?? '-1', limit: 20 } }))
            .then(value => validateMediaResults(value, version, cursor));
    }

    getCreativeResults(workspaceId: string, monitorId: string, version: string, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}/creative`,
            requestConfig(workspaceId, this.idFactory(), { signal }))
            .then(value => validateCreativeResults(value, version));
    }

    getMonitorUsage(workspaceId: string, monitorId: string, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/usage`,
            requestConfig(workspaceId, this.idFactory(), { signal })).then(value => validateMonitorUsage(value, monitorId));
    }

    getCollectionOverview(workspaceId: string, timeZone: string, signal?: AbortSignal) {
        return this.api.get<unknown>('/api/market-intelligence/collection-overview',
            requestConfig(workspaceId, this.idFactory(), { signal, params: { time_zone: timeZone } }))
            .then(value => validateCollectionOverview(value, workspaceId, timeZone));
    }

    getCollectionContents(workspaceId: string, timeZone: string, asOf: string, filters: CollectionContentFilter, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>('/api/market-intelligence/collection-contents',
            requestConfig(workspaceId, this.idFactory(), { signal, params: { time_zone: timeZone, as_of: asOf, ...filters, cursor: cursor ?? undefined, limit: 30 } }))
            .then(value => validateCollectionContents(value, workspaceId, timeZone, asOf, cursor));
    }

    getLandingResults(workspaceId: string, monitorId: string, version: string, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}/landing`,
            requestConfig(workspaceId, this.idFactory(), { signal, params: { after: cursor ?? '-1', limit: 10 } }))
            .then(value => validateLandingResults(value, version, cursor));
    }

    getMediaInspectionResults(workspaceId: string, monitorId: string, version: string, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}/media-inspection`,
            requestConfig(workspaceId, this.idFactory(), { signal, params: { after: cursor ?? '-1', limit: 20 } }))
            .then(value => validateMediaInspectionResults(value, version, cursor));
    }

    getSocialEvidence(workspaceId: string, monitorId: string, cursor: string | null, signal?: AbortSignal, relevance?: 'related' | 'all') {
        return this.api.get<unknown>('/api/market-intelligence/context/evidence',
            requestConfig(workspaceId, this.idFactory(), { signal, params: {
                monitor_id: monitorId, observation_domain: 'social',
                ...(relevance ? { relevance } : {}),
                cursor: cursor ?? undefined, limit: 100,
            } })).then(value => validateProductEvidence(value, monitorId, 'social'));
    }

    getSearchEvidence(workspaceId: string, monitorId: string, cursor: string | null, signal?: AbortSignal, relevance?: 'related' | 'all') {
        return this.api.get<unknown>('/api/market-intelligence/context/evidence',
            requestConfig(workspaceId, this.idFactory(), { signal, params: {
                monitor_id: monitorId, observation_domain: 'search',
                ...(relevance ? { relevance } : {}), cursor: cursor ?? undefined, limit: 100,
            } })).then(value => validateProductEvidence(value, monitorId, 'search'));
    }

    getAdEvidence(workspaceId: string, monitorId: string, cursor: string | null, signal?: AbortSignal) {
        return this.api.get<unknown>('/api/market-intelligence/context/evidence',
            requestConfig(workspaceId, this.idFactory(), { signal, params: {
                monitor_id: monitorId, observation_domain: 'ad', source_system: 'meta_ad_library',
                cursor: cursor ?? undefined, limit: 100,
            } })).then(value => validateProductEvidence(value, monitorId, 'ad'));
    }

    getInsightEvidence(workspaceId: string, monitorId: string, version: string, cursor: string | null, signal?: AbortSignal, evidenceIds?: string[]) {
        return this.api.get<unknown>(`/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/insights/${encodeURIComponent(version)}/evidence`,
            requestConfig(workspaceId, this.idFactory(), { signal, params: { after: cursor ?? '-1', limit: 100, evidence_ids: evidenceIds?.join(',') } }))
            .then(value => validateInsightEvidence(value, { version, evidenceIds }));
    }

    constructor(
        apiConfig: ApiConfig,
        idFactory: IdFactory = createMarketIntelligenceUuid,
    ) {
        this.api = new Api(apiConfig);
        this.idFactory = idFactory;
    }

    listMonitors(
        workspaceId: string,
        signal?: AbortSignal,
    ): Promise<MarketMonitor[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/monitors',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then((value) => {
                if (!Array.isArray(value))
                    throw new MarketIntelligenceResponseError('monitor list');
                return value.map(validateMonitor);
            });
    }

    getRuntimeStatus(
        workspaceId: string,
        signal?: AbortSignal,
    ): Promise<MarketRuntimeStatus> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/runtime-status',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(validateRuntimeStatus);
    }

    listCapabilities(
        workspaceId: string,
        signal?: AbortSignal,
    ): Promise<MarketCapability[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/capabilities',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then((value) => {
                if (!Array.isArray(value))
                    throw new MarketIntelligenceResponseError(
                        'capability list',
                    );
                return value.map(validateCapability);
            });
    }

    async putMonitor(
        workspaceId: string,
        monitorId: string,
        monitor: MarketMonitorWrite,
        version?: number,
        signal?: AbortSignal,
        previousSourceScopes: MarketMonitorSourceScope[] = [],
    ): Promise<MarketMonitor> {
        // Read-only negotiation prevents old servers ignoring a total and creating per-slice budgets.
        if (Object.prototype.hasOwnProperty.call(monitor, 'monitoring_period') || Object.prototype.hasOwnProperty.call(monitor, 'notification_config') || Object.prototype.hasOwnProperty.call(monitor, 'report_config') || Object.prototype.hasOwnProperty.call(monitor, 'landing_config') || Object.prototype.hasOwnProperty.call(monitor, 'media_inspection_config') || Object.prototype.hasOwnProperty.call(monitor, 'creative_config') || Object.prototype.hasOwnProperty.call(monitor, 'media_config') || Object.prototype.hasOwnProperty.call(monitor, 'analysis_config') || Object.prototype.hasOwnProperty.call(monitor, 'business_subject') || monitor.identifiers.some(item => Object.prototype.hasOwnProperty.call(item, 'business_subject')) || monitor.run_budget || monitor.source_scopes.some(scope =>
            scope.capability === 'meta.ads.discover_by_advertiser' || scope.capability === 'amazon.products.collect_by_url' || scope.media_type != null || scope.active_status != null
            || scope.collection_history !== undefined || scope.include_public_details != null || scope.post_window != null || scope.advertiser_period != null || scope.advertiser_sort != null
            || scope.result_filters != null || scope.result_sort != null || scope.result_grouping != null
            || ['facebook.posts.discover_by_account', 'instagram.posts.discover_by_account'].includes(scope.capability) || isYoutubeCapability(scope.capability) || isMetaCapability(scope.capability))
            || monitor.source_scopes.some(scope => isAmazonCapability(scope.capability) || scope.capability === AMAZON_REVIEWS_CAPABILITY)
            || requestsDiscoveryDates(previousSourceScopes) || requestsDiscoveryDates(monitor.source_scopes)
            || needsCommentHistoryPreview(monitor.source_scopes, previousSourceScopes))
            await this.previewMonitor(workspaceId, monitor, signal, previousSourceScopes, version !== undefined ? monitorId : undefined);
        const extra: AxiosRequestConfig = { signal };
        if (version !== undefined) {
            extra.headers = { 'If-Match': `\"mi-v${version}\"` };
        }
        return this.api
            .put<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}`,
                monitor,
                requestConfig(workspaceId, this.idFactory(), extra),
            )
            .then(validateMonitor)
            .then(value => {
                if (!monitoringPeriodMatches(monitor.monitoring_period, value.monitoring_period))
                    throw new MarketIntelligenceResponseError('saved monitoring period');
                if (!storeAuthoringMatches(monitor, value as unknown as Record<string, unknown>))
                    throw new MarketIntelligenceResponseError('saved Amazon store configuration');
                if (!amazonFilterScopesMatch(monitor.source_scopes, value.source_scopes ?? []))
                    throw new MarketIntelligenceResponseError('saved Amazon filters');
                if (!amazonReviewScopesMatch(monitor.source_scopes, value.source_scopes ?? []))
                    throw new MarketIntelligenceResponseError('saved Amazon review configuration');
                if (monitor.monitoring_period == null
                    && !collectionHistoryScopesMatch(monitor.source_scopes, value.source_scopes, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('saved collection history');
                if (!commentHistoryScopesMatch(monitor.source_scopes, value.source_scopes, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('saved comment history');
                if (monitor.monitoring_period == null
                    && !discoveryDateScopesMatch(monitor.source_scopes, value.source_scopes, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('saved discovery dates');
                if (!notificationConfigMatches(monitor.notification_config, value.notification_config))
                    throw new MarketIntelligenceResponseError('saved notification configuration');
                if (!reportConfigMatches(monitor.report_config, value.report_config))
                    throw new MarketIntelligenceResponseError('saved report schedule');
                if (!landingConfigMatches(monitor.landing_config, value.landing_config))
                    throw new MarketIntelligenceResponseError('saved landing configuration');
                if (!mediaInspectionConfigMatches(monitor.media_inspection_config, value.media_inspection_config))
                    throw new MarketIntelligenceResponseError('saved media inspection configuration');
                if (!creativeConfigMatches(monitor.creative_config, value.creative_config))
                    throw new MarketIntelligenceResponseError('saved creative configuration');
                if (!mediaConfigMatches(monitor.media_config, value.media_config))
                    throw new MarketIntelligenceResponseError('saved media configuration');
                if (!analysisConfigMatches(monitor.analysis_config, value.analysis_config))
                    throw new MarketIntelligenceResponseError('saved analysis budget');
                return value;
            });
    }

    retireMonitor(
        workspaceId: string,
        monitorId: string,
        version: number,
        idempotencyKey: string,
        signal?: AbortSignal,
    ): Promise<void> {
        return this.api.delete<void>(
            `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}`,
            requestConfig(workspaceId, this.idFactory(), {
                signal,
                headers: {
                    'If-Match': `\"mi-v${version}\"`,
                    'Idempotency-Key': idempotencyKey,
                },
            }),
        );
    }

    previewMonitor(
        workspaceId: string,
        monitor: MarketMonitorWrite,
        signal?: AbortSignal,
        previousSourceScopes: MarketMonitorSourceScope[] = [],
        existingMonitorId?: string,
    ): Promise<MarketMonitorPreview> {
        const addressedHistory = existingMonitorId && (needsNativeHistoryPreview(monitor.source_scopes, previousSourceScopes)
            || needsCommentHistoryPreview(monitor.source_scopes, previousSourceScopes));
        return this.api
            .post<unknown>(
                addressedHistory ? `/api/market-intelligence/monitors/${encodeURIComponent(existingMonitorId)}/preview`
                    : '/api/market-intelligence/monitors/preview',
                monitor,
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(value => validateMonitorPreview(value))
            .then(value => {
                if (!monitoringPeriodMatches(monitor.monitoring_period, value.normalized_monitor.monitoring_period))
                    throw new MarketIntelligenceResponseError('monitoring period negotiation');
                if (!storeAuthoringMatches(monitor, value.normalized_monitor))
                    throw new MarketIntelligenceResponseError('Amazon store configuration negotiation');
                if (!amazonReviewScopesMatch(monitor.source_scopes, value.normalized_monitor.source_scopes ?? []))
                    throw new MarketIntelligenceResponseError('Amazon review configuration negotiation');
                if (monitor.monitoring_period == null
                    && !collectionHistoryPreviewMatches(monitor.source_scopes, value, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('collection history negotiation');
                if (!commentHistoryPreviewMatches(monitor.source_scopes, value, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('comment history negotiation');
                if (monitor.monitoring_period == null
                    && !discoveryDatePreviewMatches(monitor.source_scopes, value, previousSourceScopes))
                    throw new MarketIntelligenceResponseError('discovery date negotiation');
                if (!notificationConfigMatches(monitor.notification_config, value.normalized_monitor.notification_config))
                    throw new MarketIntelligenceResponseError('notification configuration negotiation');
                if (!reportConfigMatches(monitor.report_config, value.normalized_monitor.report_config))
                    throw new MarketIntelligenceResponseError('report schedule negotiation');
                if (!mediaInspectionConfigMatches(monitor.media_inspection_config, value.normalized_monitor.media_inspection_config))
                    throw new MarketIntelligenceResponseError('media inspection configuration negotiation');
                if (!creativeConfigMatches(monitor.creative_config, value.normalized_monitor.creative_config))
                    throw new MarketIntelligenceResponseError('creative configuration negotiation');
                if (!landingConfigMatches(monitor.landing_config, value.normalized_monitor.landing_config))
                    throw new MarketIntelligenceResponseError('landing configuration negotiation');
                if (!mediaConfigMatches(monitor.media_config, value.normalized_monitor.media_config))
                    throw new MarketIntelligenceResponseError('media configuration negotiation');
                if (!analysisConfigMatches(monitor.analysis_config, value.normalized_monitor.analysis_config))
                    throw new MarketIntelligenceResponseError('analysis budget negotiation');
                if (!businessSubjectDeclarationsMatch(monitor, value.normalized_monitor))
                    throw new MarketIntelligenceResponseError('Business subject declaration negotiation');
                if (!amazonProductScopeEchoesRequest(monitor, value))
                    throw new MarketIntelligenceResponseError('Amazon product scope negotiation');
                if (!youtubeTargetScopeEchoesRequest(monitor, value))
                    throw new MarketIntelligenceResponseError('YouTube target scope negotiation');
                for (const scope of monitor.source_scopes) {
                    const defaults = { media_type: 'all', active_status: 'active', include_public_details: false,
                        post_window: 'past_week', advertiser_period: 'last7d', advertiser_sort: 'most_recent', result_sort: 'collection_order', result_grouping: 'ads' } as const;
                    const configured = (Object.keys(defaults) as Array<keyof typeof defaults>).filter(key => scope[key] != null);
                    if (!configured.length && scope.max_records_per_slice == null && scope.result_filters == null && scope.capability !== 'meta.ads.discover_by_advertiser'
                        && !['reddit.posts.discover_by_keyword', 'reddit.posts.discover_by_subreddit'].includes(scope.capability)
                        && !isYoutubeCapability(scope.capability)
                        && !isAmazonCapability(scope.capability)
                        && !['facebook.posts.discover_by_account', 'instagram.posts.discover_by_account'].includes(scope.capability) && !isMetaCapability(scope.capability)) continue;
                    const echoed = value.normalized_monitor.source_scopes;
                    const matches = Array.isArray(echoed) ? echoed.filter(candidate =>
                        isRecord(candidate) && candidate.capability === scope.capability
                        && candidate.source_system === scope.source_system && isRecord(candidate.scope)
                        && candidate.scope.kind === scope.scope.kind
                        && candidate.scope.value === scope.scope.value) : [];
                    if (matches.length !== 1) throw new MarketIntelligenceResponseError('channel option negotiation');
                    const expectedLimit = scope.capability === 'x.posts.search_recent' && scope.max_records_per_slice != null
                        ? Math.max(50, scope.max_records_per_slice) : scope.max_records_per_slice;
                    if (expectedLimit != null && matches[0].max_records_per_slice !== expectedLimit)
                        throw new MarketIntelligenceResponseError('channel record limit negotiation');
                    for (const key of configured) {
                        if (!monitoringChannelOptionMatches(scope.capability, key, scope[key],
                            matches[0][key] ?? defaults[key], monitor.monitoring_period != null))
                            throw new MarketIntelligenceResponseError('channel option negotiation');
                    }
                    if (['reddit.posts.discover_by_keyword', 'reddit.posts.discover_by_subreddit'].includes(scope.capability)) {
                        const requested = scope.result_filters ?? {};
                        const actual = isRecord(matches[0].result_filters) ? matches[0].result_filters : {};
                        const names = (values: unknown) => Array.isArray(values)
                            ? [...new Set(values.map(value => String(value).trim().replace(/^\/?r\//i, '').toLowerCase()))].sort() : [];
                        const languages = (values: unknown) => Array.isArray(values)
                            ? [...new Set(values.map(value => String(value).trim().toLowerCase()))].sort() : [];
                        if (JSON.stringify(names(actual.subreddits)) !== JSON.stringify(names(requested.subreddits))
                            || JSON.stringify(languages(actual.languages)) !== JSON.stringify(languages(requested.languages))
                            || (actual.min_score ?? null) !== (requested.min_score ?? null)
                            || (actual.match_mode ?? null) !== (requested.match_mode ?? null)
                            || (actual.published_from ?? null) !== (requested.published_from ?? null)
                            || (actual.published_to ?? null) !== (requested.published_to ?? null))
                            throw new MarketIntelligenceResponseError('result filter negotiation');
                    }
                    if (isMetaCapability(scope.capability)) {
                        try {
                            if ((scope.result_sort ?? 'collection_order') !== (matches[0].result_sort ?? 'collection_order')
                                || (scope.result_grouping ?? 'ads') !== (matches[0].result_grouping ?? 'ads'))
                                throw new Error('Changed display options');
                            if (JSON.stringify(normalizeMetaResultFilters(scope.result_filters)) !== JSON.stringify(normalizeMetaResultFilters(matches[0].result_filters)))
                                throw new Error('Changed conditions');
                        } catch { throw new MarketIntelligenceResponseError('Meta result filter negotiation'); }
                    }
                    if (isYoutubeCapability(scope.capability)) {
                        try {
                            if (JSON.stringify(normalizeYoutubeResultFilters(scope.result_filters)) !== JSON.stringify(normalizeYoutubeResultFilters(matches[0].result_filters)))
                                throw new Error('Changed conditions');
                        } catch { throw new MarketIntelligenceResponseError('YouTube result filter negotiation'); }
                    }
                    if (isAmazonCapability(scope.capability)) {
                        try {
                            if (JSON.stringify(normalizeAmazonResultFilters(scope.result_filters)) !== JSON.stringify(normalizeAmazonResultFilters(matches[0].result_filters)))
                                throw new Error('Changed conditions');
                        } catch { throw new MarketIntelligenceResponseError('Amazon result filter negotiation'); }
                    }
                    if (scope.capability === 'facebook.posts.discover_by_account' || scope.capability === 'instagram.posts.discover_by_account'
                        || scope.capability === 'facebook.posts.discover_by_keyword' || scope.capability === 'instagram.posts.discover_by_keyword') {
                        const source = scope.capability.startsWith('facebook') ? 'facebook' : 'instagram';
                        try {
                            if (JSON.stringify(normalizeAccountPostFilters(source, scope.result_filters)) !== JSON.stringify(normalizeAccountPostFilters(source, matches[0].result_filters)))
                                throw new Error('Changed conditions');
                        } catch { throw new MarketIntelligenceResponseError('account post filter negotiation'); }
                    }
                }
                if (monitor.run_budget) {
                    const echoed = value.normalized_monitor.run_budget;
                    const xScopes = monitor.source_scopes.filter(scope => scope.capability === 'x.posts.search_recent');
                    const xTerms = monitor.terms.filter(term => term.role !== 'exclude').length;
                    const requestedRecords = monitor.run_budget.max_records;
                    const effectiveRecords = isRecord(echoed) ? echoed.max_records : null;
                    const xMinimumAdjustment = xScopes.reduce((total, scope) =>
                        total + Math.max(0, 50 - (scope.max_records_per_slice ?? 50)) * xTerms, 0);
                    const xMinimumBudget = xScopes.length * xTerms * 50;
                    const keywordMinimum = minimumKeywordRunRecords(monitor);
                    const maximumRecords = xScopes.length
                        ? keywordMinimum !== null
                            ? Math.max(requestedRecords, keywordMinimum)
                            : Math.max(requestedRecords + xMinimumAdjustment, xMinimumBudget)
                        : requestedRecords;
                    if (!isRecord(echoed) || echoed.contract_version !== monitor.run_budget.contract_version
                        || echoed.unit !== monitor.run_budget.unit || !Number.isInteger(effectiveRecords)
                        || typeof effectiveRecords !== 'number' || effectiveRecords < requestedRecords
                        || effectiveRecords > maximumRecords
                        || value.max_records > Number(effectiveRecords))
                        throw new MarketIntelligenceResponseError('run budget negotiation');
                }
                return value;
            });
    }

    updateMonitorStatus(
        workspaceId: string,
        monitorId: string,
        enabled: boolean,
        version: number,
        signal?: AbortSignal,
    ): Promise<MarketMonitor> {
        return this.api
            .patch<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/status`,
                { enabled },
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    headers: { 'If-Match': `\"mi-v${version}\"` },
                }),
            )
            .then(validateMonitor);
    }

    getMonitorSummary(
        workspaceId: string,
        monitorId: string,
        heatWindow: MarketHeatWindow = '7d',
        signal?: AbortSignal,
    ): Promise<MarketMonitorSummary> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/summary`,
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    params: { heat_window: heatWindow },
                }),
            )
            .then(validateMonitorSummary);
    }

    listMonitorSummaries(
        workspaceId: string,
        heatWindow: MarketHeatWindow = '7d',
        signal?: AbortSignal,
    ): Promise<MarketMonitorSummary[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/monitor-summaries',
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    params: { heat_window: heatWindow },
                }),
            )
            .then((value) => {
                if (!Array.isArray(value))
                    throw new MarketIntelligenceResponseError(
                        'monitor summary list',
                    );
                return value.map(validateMonitorSummary);
            });
    }

    refreshMonitor(
        workspaceId: string,
        monitorId: string,
        idempotencyKey: string,
        signal?: AbortSignal,
    ): Promise<MarketQuery> {
        return this.api
            .post<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/refresh`,
                undefined,
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    headers: { 'Idempotency-Key': idempotencyKey },
                }),
            )
            .then(validateQuery);
    }

    getQuery(
        workspaceId: string,
        queryId: string,
        signal?: AbortSignal,
    ): Promise<MarketQuery> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/queries/${encodeURIComponent(queryId)}`,
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(validateQuery);
    }

    getStoreCandidates(workspaceId: string, monitorId: string, queryId: string, cursor?: string, signal?: AbortSignal): Promise<AmazonStoreCandidatePage> {
        return this.api.get<unknown>(
            `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/runs/${encodeURIComponent(queryId)}/store-candidates`,
            requestConfig(workspaceId, this.idFactory(), { signal, params: { limit: 25, ...(cursor ? { cursor } : {}) } }),
        ).then(value => validateStoreCandidates(value, monitorId, queryId, cursor));
    }

    listRuns(
        workspaceId: string,
        monitorId: string,
        limit = 20,
        signal?: AbortSignal,
    ): Promise<MarketQuery[]> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/runs`,
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    params: { limit },
                }),
            )
            .then((value) => {
                if (!Array.isArray(value))
                    throw new MarketIntelligenceResponseError('run history');
                return value.map(validateQuery);
            });
    }

    listWatchTargets(
        workspaceId: string,
        signal?: AbortSignal,
    ): Promise<MarketWatchTarget[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/watch-targets',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then((value) => {
                if (!Array.isArray(value))
                    throw new MarketIntelligenceResponseError(
                        'watch target list',
                    );
                return value.map(validateWatchTarget);
            });
    }

    putWatchTarget(
        workspaceId: string,
        targetId: string,
        target: MarketWatchTargetWrite,
        version?: number,
        signal?: AbortSignal,
    ): Promise<MarketWatchTarget> {
        const extra: AxiosRequestConfig = { signal };
        if (version !== undefined)
            extra.headers = { 'If-Match': `\"${version}\"` };
        return this.api
            .put<unknown>(
                `/api/market-intelligence/watch-targets/${encodeURIComponent(targetId)}`,
                target,
                requestConfig(workspaceId, this.idFactory(), extra),
            )
            .then(validateWatchTarget);
    }

    getTargetHeat(
        workspaceId: string,
        targetId: string,
        window = '7d',
        signal?: AbortSignal,
    ): Promise<MarketHeat> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/watch-targets/${encodeURIComponent(targetId)}/heat`,
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    params: { window },
                }),
            )
            .then(validateHeat);
    }
}

export const marketIntelligenceService = new MarketIntelligenceService({
    baseURL: config.services.auth.apiHost,
});
